
/* ==========================================================================
   DESKTOP WORKSTATION CARD GENERATORS (/database/js/card-desktop.js)
   Namespace: .ws-* (Workstation Unified Component Architecture)
   ========================================================================== */

import { escapeHtml } from '../utils.js?v=20260819-audit2';
import {
  getCaseCardViewModel, renderModeSelectorHtml, renderFormFactorSummaryHtml,
  renderClearancePanelHtml, renderSecondaryDataHtml, renderLayerBarHtml
} from './card-shared.js?v=20260819-audit2';
export { updateCardClearanceGauges as updateDesktopCardGauges } from './card-shared.js?v=20260819-audit2';

function buildWorkstationCardHtml(c, idx, cardType = 'feed') {
  const vm = getCaseCardViewModel(c, idx, { cardType, surface: 'desktop' });
  const single = cardType === 'single';
  const wrapperClass = single
    ? `case-card ws-card-feed ws-card-hero single-case-focus ${vm.isSelected ? 'selected-case' : ''}`
    : `case-card ws-card-feed ${vm.isSelected ? 'selected-case' : ''} ${vm.isPinned ? 'pinned-case' : ''}`;
  const broadNavAttr = vm.broadNavigation ? ` data-card-nav-slug="${vm.slug}"` : '';
  const lazyDrawersMarkup = cardType === 'feed' ? '' : `
    <div class="ws-community-builds-drawer font-mono" id="${vm.buildsDrawerId}" style="display:none;" aria-hidden="true"></div>
    <div class="ws-reviews-drawer font-mono" id="${vm.reviewsDrawerId}" style="display:none;" aria-hidden="true"></div>`;

  const titleMarkup = single
    ? `<h3 class="ws-case-title">${escapeHtml(vm.formattedTitle)}</h3>`
    : `<a class="ws-case-title ws-case-nav-link" href="${vm.href}" data-slug="${vm.slug}">${escapeHtml(vm.formattedTitle)}</a>`;
  const thumbMarkup = single
    ? `<div class="ws-thumb-container"><img data-src="${escapeHtml(c.image)}" src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" alt="${escapeHtml(c.name)}" class="case-thumb lazy-img-node" onerror="this.onerror=null;this.src='/assets/images/placeholder.webp';"></div>`
    : `<a class="ws-thumb-container ws-case-nav-link" href="${vm.href}" data-slug="${vm.slug}"><img data-src="${escapeHtml(c.image)}" src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" alt="${escapeHtml(c.name)}" class="case-thumb lazy-img-node" onerror="this.onerror=null;this.src='/assets/images/placeholder.webp';"></a>`;

  let pinText = vm.isPinned ? 'PINNED' : 'PIN';
  let validateText = 'VALIDATE';
  let volumeText = `${escapeHtml(c.volume)}L`;
  let fitText = 'FITS';
  if (single) {
    pinText = vm.isPinned ? 'PINNED' : 'PIN TO COMPARE';
    validateText = 'VALIDATE CASE';
    volumeText = `${escapeHtml(c.volume)} LITERS`;
    fitText = 'VERIFIED FIT';
  } else if (cardType === 'compare') {
    pinText = 'UNPIN';
  }

  return `
    <div class="${wrapperClass}" id="card-${idx}" data-card-type="${cardType}"${broadNavAttr}>
      <div class="ws-card-header font-mono">
        <div class="ws-header-title-block">
          ${titleMarkup}
          <div class="ws-header-badges">
            <span class="status-label status-badge fits">${fitText}</span>
            <span class="tag-badge volume-display-tag">${volumeText}</span>
            <span class="status-badge purple-layout">${escapeHtml(vm.layoutTag)}</span>
            ${c.hasSpine ? '<span class="status-badge spine-badge">ADJ SPINE</span>' : ''}
            ${vm.hasModes ? `<span class="status-badge mode-badge">${c.modes.length} MODES</span>` : ''}
            ${c._isLegacy ? '<span class="status-badge legacy-badge">LEGACY</span>' : ''}
          </div>
        </div>

        <div class="ws-header-toolbar">
          <button type="button" class="sff-pin-card-btn ${vm.isPinned ? 'active-pin' : ''}" data-pin-idx="${idx}">
            <svg viewBox="0 0 24 24" fill="${vm.isPinned ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px;"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
            <span>${pinText}</span>
          </button>
          <button type="button" class="sff-validate-card-btn ${vm.isSelected ? 'selected' : ''}" data-idx="${idx}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px;"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
            <span>${validateText}</span>
          </button>
          <a class="case-external-action-link ${vm.linkClass}" href="${vm.safeUrl}" target="_blank" rel="noopener noreferrer">
            <span>${vm.linkText}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:11px;height:11px;"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
          </a>
        </div>
      </div>

      <div class="ws-card-body">
        <div class="ws-media-col">
          ${thumbMarkup}
          ${renderModeSelectorHtml(vm)}
        </div>

        <div class="ws-spec-col">
          ${renderFormFactorSummaryHtml(vm)}
          ${renderClearancePanelHtml(vm)}
          ${renderSecondaryDataHtml(vm)}
          ${renderLayerBarHtml(vm)}
        </div>
      </div>

      <div class="spine-status-bar-restored" id="spine-bar-${idx}">
        <span class="spine-mode-title" id="spine-title-${idx}">Adjustable Spine Mode Active</span>
        <span class="spine-mode-specs" id="spine-specs-${idx}"></span>
      </div>
      <div class="card-footer-stack"><p class="diagnostic-log"></p></div>
      ${lazyDrawersMarkup}
    </div>`;
}

export function renderDesktopFeedCard(c, idx) {
  const container = document.createElement('div');
  container.innerHTML = buildWorkstationCardHtml(c, idx, 'feed');
  return container.firstElementChild;
}

export function renderDesktopSingleCard(c, idx) {
  const container = document.createElement('div');
  container.innerHTML = buildWorkstationCardHtml(c, idx, 'single');
  return container.firstElementChild;
}

export function renderDesktopCompareCard(c, idx) {
  const container = document.createElement('div');
  container.innerHTML = buildWorkstationCardHtml(c, idx, 'compare');
  return container.firstElementChild;
}
